/**
 * The microphones and outputs this browser offers, for Settings › Voice's
 * pickers and the quick device menus on mute and deafen (hub/voiceQuick.ts):
 * one enumeration, kept fresh by `devicechange`.
 */
import { useEffect, useState } from 'react';

/** Every media device now, or none where the browser lists none (or refuses). */
export async function listDevices(): Promise<MediaDeviceInfo[]> {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return [];
  try {
    return await md.enumerateDevices();
  } catch {
    return [];
  }
}

/** Calls `fn` when a device comes or goes; returns the stop. */
export function onDevicesChange(fn: () => void): () => void {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  md?.addEventListener?.('devicechange', fn);
  return () => md?.removeEventListener?.('devicechange', fn);
}

/** The output can be chosen here (`setSinkId`; Safari and Firefox on Android have none). */
export function canPickOutput(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/** The devices, listed again on `devicechange` or `refresh` (once names are allowed). */
export function useDevices() {
  const [list, setList] = useState<MediaDeviceInfo[]>([]);
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    void listDevices().then((l) => live && setList(l));
    const off = onDevicesChange(() => setN((x) => x + 1));
    return () => {
      live = false;
      off();
    };
  }, [n]);
  return { list, refresh: () => setN((x) => x + 1) };
}
